import { z } from 'zod';

// Дизайны tick-плагина: плита + поля синхронизируются между всеми, из каждого
// опубликованного дизайна плагин делает кнопку. Подробности и жизненный цикл —
// в миграции 0038_tick_designs.sql.

export const DevDesignStatusSchema = z.enum(['draft', 'published']);
export type DevDesignStatus = z.infer<typeof DevDesignStatusSchema>;

export const DevDesignFontSchema = z.object({
  family: z.string().trim().min(1).max(120),
  style: z.string().trim().max(60).default('Regular'),
});
export type DevDesignFont = z.infer<typeof DevDesignFontSchema>;

// Поле шаблона: то, что в Figma лежит как field_<key> [ __mask + __value ].
//
// passthrough тут ОБЯЗАТЕЛЕН, а не «на всякий случай». Кроме геометрии и
// типографики поле несёт флаги поведения на батче — maxLines, fitLen, wrapAdv,
// skipIfEmpty, flow, boldRanges, noMask. Во фрейме Figma их не видно, дизайнер их
// не трогает, и если zod срежет незнакомый ключ, превью останется красивым, а
// батч на 900 строк полезет за маски. Поэтому валидируем только то, без чего
// нельзя нарисовать, а остальное проносим как есть.
// Строковые ключи принимают null, а не только отсутствие. Рендер tick отдаёт
// `family: null` у поля без своего шрифта (оно наследует шрифт провайдера), и
// строгая строка здесь означала бы 400 на каждом сохранении такого шаблона.
const optStr = (max: number) => z.string().trim().max(max).nullish().transform(v => v || '');

export const DevDesignFieldSchema = z.object({
  key: z.string().trim().min(1).max(120),
  value: z.string().max(4000).nullish().transform(v => v || ''),
  x: z.number(),
  y: z.number(),
  w: z.number().nonnegative(),
  h: z.number().nonnegative(),
  family: optStr(120),
  style: optStr(60),
  size: z.number().nonnegative().default(0),
  color: optStr(32),
}).passthrough();
export type DevDesignField = z.infer<typeof DevDesignFieldSchema>;

// Слот кода: code | barcode | barcode_v. Значение не возим — плагин рисует код
// сам (renderPlainQR/renderCode128/renderEAN13), нужны бокс и параметры символогии.
export const DevDesignCodeSlotSchema = z.object({
  slot: z.string().trim().min(1).max(40),
  x: z.number(),
  y: z.number(),
  w: z.number().nonnegative(),
  h: z.number().nonnegative(),
}).passthrough();
export type DevDesignCodeSlot = z.infer<typeof DevDesignCodeSlotSchema>;

export const DevDesignSpecSchema = z.object({
  frame: z.object({
    w: z.number().positive().max(20000),
    h: z.number().positive().max(20000),
  }),
  fields: z.array(DevDesignFieldSchema).min(1).max(400),
  codes: z.array(DevDesignCodeSlotSchema).max(8).default([]),
  fonts: z.array(DevDesignFontSchema).max(60).default([]),
}).passthrough();
export type DevDesignSpec = z.infer<typeof DevDesignSpecSchema>;

export const DevDesignSchema = z.object({
  id: z.string(),
  design_key: z.string(),
  label: z.string(),
  locale: z.string(),
  merchant: z.string(),
  card_id: z.string(),
  status: DevDesignStatusSchema,
  version: z.number().int().nonnegative(),       // последняя загруженная автором
  live_version: z.number().int().nonnegative(),  // та, что отдаётся всем; 0 = не выкачена
  frame_w: z.number().int().nonnegative(),
  frame_h: z.number().int().nonnegative(),
  field_count: z.number().int().nonnegative(),
  fonts: z.array(DevDesignFontSchema),
  author_email: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DevDesign = z.infer<typeof DevDesignSchema>;

// «Сохранить дизайн» в плагине. Апсерт по (автор, design_key): дизайнер жмёт эту
// кнопку много раз, пока правит шрифты, и плодить черновики не надо.
export const DevDesignSaveReqSchema = z.object({
  design_key: z.string().trim().regex(/^[a-z0-9][a-z0-9_]*$/, 'lowercase_snake').min(2).max(64),
  label: z.string().trim().min(1).max(120),
  locale: z.string().trim().max(16).default(''),
  merchant: z.string().trim().max(120).default(''),
  spec: DevDesignSpecSchema,
  plate_base64: z.string().min(1).max(40_000_000),
  plate_content_type: z.string().trim().max(120).default('image/png'),
});
export type DevDesignSaveReq = z.infer<typeof DevDesignSaveReqSchema>;

export const DevDesignQuerySchema = z.object({
  id: z.string().trim().min(1).max(64),
});
export type DevDesignQuery = z.infer<typeof DevDesignQuerySchema>;

export const DevDesignListQuerySchema = z.object({
  status: z.union([DevDesignStatusSchema, z.literal('')]).default(''),
});
export type DevDesignListQuery = z.infer<typeof DevDesignListQuerySchema>;

// Привязка к донору = публикация: с этого момента кнопка появляется у всех.
export const DevDesignBindReqSchema = z.object({
  design_id: z.string().trim().min(1).max(64),
  card_id: z.string().trim().min(1).max(64),
  replace: z.boolean().default(false),   // true = «Заменить дизайн» на уже занятом доноре
});
export type DevDesignBindReq = z.infer<typeof DevDesignBindReqSchema>;

export const DevDesignResSchema = z.object({ ok: z.boolean(), design: DevDesignSchema });
export type DevDesignRes = z.infer<typeof DevDesignResSchema>;

export const DevDesignListResSchema = z.object({
  ok: z.boolean(),
  designs: z.array(DevDesignSchema),
});
export type DevDesignListRes = z.infer<typeof DevDesignListResSchema>;

export const DevDesignSpecResSchema = z.object({
  ok: z.boolean(),
  design: DevDesignSchema,
  spec: DevDesignSpecSchema,
});
export type DevDesignSpecRes = z.infer<typeof DevDesignSpecResSchema>;
